/**
 * Global PWA Install Manager
 * 
 * Captures beforeinstallprompt event immediately on page load
 * and stores it for components to access later.
 * This solves the timing issue where the event fires before React mounts.
 */

let deferredPrompt = null;
let listeners = [];
let isInitialized = false;

/**
 * Initialize the global PWA install listener
 * Call this once at app startup (in index.js)
 */
export function initPWAInstallListener() {
  if (isInitialized) return;
  isInitialized = true;

  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    
    // Store the event for later use
    deferredPrompt = e;
    
    // Notify all subscribers
    listeners.forEach((callback) => callback(true));
    
    console.log("[PWA] Install prompt captured and stored");
  });

  // Listen for app installed event
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((callback) => callback(false));
    console.log("[PWA] App was installed");
  });
}

/**
 * Subscribe to install availability changes
 * @param {Function} callback - Called with true when install is available, false when not
 * @returns {Function} Unsubscribe function
 */
export function subscribeInstallAvailability(callback) {
  listeners.push(callback);
  
  // Immediately notify if we already have a deferred prompt
  if (deferredPrompt) {
    callback(true);
  }
  
  // Return unsubscribe function
  return () => {
    listeners = listeners.filter((cb) => cb !== callback);
  };
}

/**
 * Trigger the install prompt
 * @returns {Promise<{outcome: string}>|null} User choice or null if not available
 */
export async function triggerInstall() {
  if (!deferredPrompt) {
    console.warn("[PWA] No install prompt available");
    return null;
  }

  // Show the prompt
  deferredPrompt.prompt();
  
  // Wait for user choice
  const choiceResult = await deferredPrompt.userChoice;
  
  // Clear the deferred prompt regardless of outcome
  deferredPrompt = null;
  
  // Notify listeners
  listeners.forEach((callback) => callback(false));
  
  console.log("[PWA] Install prompt result:", choiceResult.outcome);
  return choiceResult;
}

/**
 * Check if install is currently available
 * @returns {boolean}
 */
export function isInstallAvailable() {
  return !!deferredPrompt;
}